import React, { useEffect, useState, useCallback } from 'react'
import { Typography, useTheme } from '@material-ui/core'
import { Container } from '../../../../components'
import { ScheduleCalendar } from './components/ScheduleCalendar'
import { DateClickPopover } from './components/DateClickPopover'
import { ScheduleModal } from './components/ScheduleModal'
import { ModalState, PopoverState } from './types'
import { useUser } from '../../../../hooks'
import {
    useAuditEvents,
    useAuditSchedules,
    useLeaseAudit,
} from '../../../../hooks/documentAudit'
import { Routes } from '../../../../navigation/config/routes'
import { useHistory } from 'react-router-dom'

import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { axiosInstance } from '../../../../helpers'
import { LeaseAuditSchedule } from '../../../../models'

export const OrganizationSchedule = () => {
    const theme = useTheme()

    const { actions, organization, organizationCompanyList } = useUser()

    const {
        leaseAuditSchedules,
        isLoadingSchedules,
        errorSchedules,
        createSchedule,
        updateScheduleStatus,
    } = useAuditSchedules(organization ?? -1)

    const { events } = useAuditEvents(organization ?? -1)

    const { leaseAudits, fetchLeaseAudits } = useLeaseAudit()

    const history = useHistory()

    const queryClient = useQueryClient()

    const [popoverState, setPopoverState] = useState<PopoverState>({
        xPos: 0,
        yPos: 0,
        date: new Date(),
        open: false,
        scheduleId: null,
        leaseAuditId: null,
        eventId: null,
    })

    const [modalState, setModalState] = useState<ModalState>({
        mode: null,
        formData: {
            name: '',
            description: '',
            start_date: new Date(),
            has_recurrence: false,
            recurrence_pattern: 'NONE',
            companies: [] as number[],
            id: undefined as number | undefined,
        },
    })

    const handleSubmit = useCallback(() => {
        if (!organization) return

        createSchedule.mutate({
            name: modalState.formData.name,
            description: modalState.formData.description,
            startDate: modalState.formData.start_date,
            companies: modalState.formData.companies,
            recurrencePattern: modalState.formData.recurrence_pattern,
        })

        setModalState({
            mode: null,
            formData: {
                name: '',
                description: '',
                start_date: new Date(),
                has_recurrence: false,
                recurrence_pattern: 'NONE',
                companies: [],
                id: undefined,
            },
        })
    }, [organization, modalState.formData, createSchedule])

    const handleFormChange = useCallback(
        (formData: typeof modalState.formData) => {
            setModalState((prev) => ({ ...prev, formData }))
        },
        [],
    )

    const handleCloseModal = useCallback(() => {
        setModalState((prev) => ({
            mode: null,
            formData: {
                name: '',
                description: '',
                start_date: prev.formData.start_date,
                has_recurrence: false,
                recurrence_pattern: 'NONE',
                companies: [],
                id: undefined,
            },
        }))
    }, [])

    const handleEventClick = (
        eventId: number,
        eventType: 'Standalone' | 'Event' | 'MetaSchedule',
        xPos: number,
        yPos: number,
    ) => {
        if (eventType === 'MetaSchedule') {
            setPopoverState((prev) => ({
                ...prev,
                xPos,
                yPos,
                open: true,
                scheduleId: eventId,
                leaseAuditId: null,
                eventId: null,
            }))
        } else if (eventType === 'Standalone') {
            setPopoverState((prev) => ({
                ...prev,
                xPos,
                yPos,
                open: true,
                scheduleId: null,
                leaseAuditId: eventId,
                eventId: null,
            }))
        } else if (eventType === 'Event') {
            setPopoverState((prev) => ({
                ...prev,
                xPos,
                yPos,
                open: true,
                scheduleId: null,
                leaseAuditId: null,
                eventId: eventId,
            }))
        }
    }

    const handleEditClick = (schedule: any) => {
        setPopoverState((prev) => ({ ...prev, open: false }))
        setModalState({
            mode: 'edit',
            formData: {
                ...schedule,
                has_recurrence: !!schedule.recurrence_pattern,
                start_date: new Date(schedule.start_date),
                companies: schedule.companies.map((company: any) => company.id),
                id: schedule.id,
            },
        })
    }

    // const handlePauseSchedule = async (schedule: LeaseAuditSchedule) => {
    //     // try {
    //     //     await axiosInstance.patch(
    //     //         `document-audit/schedule/${schedule.id}/`,
    //     //         {
    //     //             is_active: !schedule.is_active,
    //     //         },
    //     //     )
    //     //     queryClient.invalidateQueries({ queryKey: ['schedules'] })
    //     //     queryClient.invalidateQueries({ queryKey: ['events'] })
    //     //     toast.success(
    //     //         `Schedule ${
    //     //             schedule.is_active ? 'paused' : 'resumed'
    //     //         } successfully`,
    //     //     )
    //     // } catch (error) {
    //     //     toast.error('Failed to update schedule status')
    //     // }
    // }

    // const handleDeleteSchedule = async (schedule: LeaseAuditSchedule) => {
    //     if (
    //         window.confirm(
    //             'Are you sure you want to delete this schedule? This will prevent future events from being created.',
    //         )
    //     ) {
    //         try {
    //             await axiosInstance.delete(
    //                 `document-audit/schedule/${schedule.id}/`,
    //             )
    //             queryClient.invalidateQueries({ queryKey: ['schedules'] })
    //             queryClient.invalidateQueries({ queryKey: ['events'] })
    //             setPopoverState({ ...popoverState, open: false })
    //             toast.success('Schedule deleted successfully')
    //         } catch (error) {
    //             toast.error('Failed to delete schedule')
    //         }
    //     }
    // }

    useEffect(() => {
        actions.fetchOrganizationCompanyList(organization, 'APARTMENT')

        fetchLeaseAudits({ organizationId: organization ?? -1, eventId: -1 })
    }, [organization])

    return (
        <Container
            style={{
                width: '100%',
                height: '100%',
                flexDirection: 'column',
                padding: theme.spacing(2),
            }}
        >
            <Container
                style={{
                    height: 50,
                    width: '100%',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    marginBottom: theme.spacing(2),
                }}
            >
                <Typography variant="h5">Schedule</Typography>
            </Container>

            <ScheduleCalendar
                theme={theme}
                onDateClick={(arg) => {
                    // Check if selected date is before today (comparing just the dates, not time)
                    const today = new Date()
                    today.setHours(0, 0, 0, 0)
                    const selectedDate = new Date(arg.date)
                    selectedDate.setHours(0, 0, 0, 0)

                    if (selectedDate < today) {
                        return // Don't open popover for past dates
                    }

                    setPopoverState({
                        xPos: arg.jsEvent.clientX,
                        yPos: arg.jsEvent.clientY,
                        date: arg.date,
                        open: true,
                        scheduleId: null,
                        leaseAuditId: null,
                        eventId: null,
                    })
                }}
                schedules={leaseAuditSchedules ?? []}
                leaseAudits={leaseAudits ?? []}
                events={events ?? []}
                onEventClick={handleEventClick}
            />

            <DateClickPopover
                formState={popoverState}
                onClose={() =>
                    setPopoverState({ ...popoverState, open: false })
                }
                onScheduleClick={() => {
                    setPopoverState({ ...popoverState, open: false })
                    setModalState({
                        mode: 'create',
                        formData: {
                            name: '',
                            description: '',
                            start_date: popoverState.date,
                            has_recurrence: false,
                            recurrence_pattern: 'NONE',
                            companies: [],
                            id: undefined,
                        },
                    })
                }}
                schedule={
                    leaseAuditSchedules?.find(
                        (schedule) => schedule.id === popoverState.scheduleId,
                    ) || null
                }
                leaseAudit={
                    leaseAudits?.find(
                        (audit) => audit.id === popoverState.leaseAuditId,
                    ) || null
                }
                event={
                    events?.find(
                        (event) => event.id === popoverState.eventId,
                    ) || null
                }
                onEditClick={handleEditClick}
                onDetailClick={(schedule) => {
                    setPopoverState({ ...popoverState, open: false })
                    history.push(
                        Routes.organization.scheduleDetail.replace(
                            ':id',
                            schedule.id.toString(),
                        ),
                    )
                }}
                onLeaseAuditDetailClick={(leaseAudit) => {
                    console.log(
                        'leaseAudit',
                        Routes.organization.leaseAuditDetail.replace(
                            ':id',
                            leaseAudit.id.toString(),
                        ),
                    )
                    setPopoverState({ ...popoverState, open: false })
                    history.push(
                        Routes.organization.leaseAuditDetail.replace(
                            ':id',
                            leaseAudit.id.toString(),
                        ),
                    )
                }}
                onEventDetailClick={(event) => {
                    setPopoverState({ ...popoverState, open: false })
                    history.push(
                        Routes.organization.scheduleDetail.replace(
                            ':id',
                            event.id.toString(),
                        ),
                    )
                }}
                onPauseSchedule={(schedule) => {
                    updateScheduleStatus.mutate({
                        scheduleId: schedule.id,
                        status: 'PAUSED',
                    })
                }}
                onResumeSchedule={(schedule) => {
                    updateScheduleStatus.mutate({
                        scheduleId: schedule.id,
                        status: 'ACTIVE',
                    })
                }}
                onDeleteSchedule={(schedule) => {
                    updateScheduleStatus.mutate({
                        scheduleId: schedule.id,
                        status: 'DELETED',
                    })
                }}
            />

            <ScheduleModal
                open={modalState.mode === 'create'}
                onClose={handleCloseModal}
                scheduleForm={modalState.formData}
                onFormChange={handleFormChange}
                onSubmit={handleSubmit}
                companies={organizationCompanyList}
                mode="create"
            />

            {/* <ScheduleModal
                open={modalState.mode === 'edit'}
                onClose={handleCloseModal}
                scheduleForm={modalState.formData}
                onFormChange={(formData) =>
                    setModalState({ ...modalState, formData })
                }
                onSubmit={handleSubmit}
                companies={organizationCompanyList}
                mode="edit"
                onDelete={() => {
                    if (!modalState.formData.id) return
                    leaseAuditController.deleteOrganizationSchedule(
                        modalState.formData.id,
                    )
                    setModalState({
                        mode: null,
                        formData: {
                            name: '',
                            description: '',
                            start_date: new Date(),
                            has_recurrence: false,
                            recurrence_pattern: 'NONE',
                            companies: [],
                            id: undefined,
                        },
                    })
                }}
            /> */}
        </Container>
    )
}
